import { CustomModal } from "../../components/modal/customModal.component";
import { useAlertDialog } from "../../components/alertDialog/useAlertDialog";
import { ApiSchemaTaskDate } from "../../models/api/invoice";
import { UtilityDate } from "../../utils/dateUtils";
import { formatAMPM } from "../../utils/fromAmPm";
import { Input, Loader, SelectPicker, Toggle, DatePicker, Modal } from "rsuite";
import { ApiAppointment } from "../../core/apis/appointment";
import { useState } from "react";
import { ApiSchemaHospitalisationType } from "../../models/api/hospitalisationType";
import editIcon from "../../assets/img/edit-icon.svg";

export const InvoiceCards = ({
  invoices,
  handleInvoiceClick,
  isEditing,
}: {
  invoices?: ApiSchemaTaskDate[];
  handleInvoiceClick?: (invoice: ApiSchemaTaskDate) => void;
  isEditing?: boolean;
}) => {
  const { alertDialogCtrl, alertDialogComponent } = useAlertDialog();

  return invoices?.length ? (
    <div className="d-flex flex-wrap">
      {alertDialogComponent}
      {invoices.map((invoice, index) =>
        handleInvoiceClick ? (
          <InvoiceCardModal
            key={index}
            invoice={invoice}
            handleInvoiceClick={handleInvoiceClick}
            alertDialogCtrl={alertDialogCtrl}
            isEditing={isEditing!}
          />
        ) : (
          <InvoiceCardComponent
            key={index}
            invoice={invoice}
            isEditing={isEditing!}
          />
        )
      )}
    </div>
  ) : (
    <div className="text-center">
      <Loader />
    </div>
  );
};

function InvoiceCardModal({
  invoice,
  handleInvoiceClick,
  alertDialogCtrl,
  isEditing,
}: {
  invoice: ApiSchemaTaskDate;
  handleInvoiceClick: (
    invoice: ApiSchemaTaskDate,
    isHospitalized?: boolean,
    hospitalizationType?: ApiSchemaHospitalisationType,
    hospitalizationReason?: string
  ) => void;
  alertDialogCtrl: any;
  isEditing?: boolean;
}) {
  const [isHospitalized, setIsHospitalized] = useState(
    !!invoice.isHospitalised
  );
  const [isUnchecked, setIsUnchecked] = useState(!!invoice.isUnchecked);
  const [startTime, setStartTime] = useState(
    new Date(invoice.taskStartTime ?? "")
  );
  const [endTime, setEndTime] = useState(new Date(invoice.taskEndTime ?? ""));
  const [hospitalizationType, setHospitalizationType] = useState<
    ApiSchemaHospitalisationType | undefined | null
  >(invoice.hospitalisationType);
  const [hospitalizationReason, setHospitalizationReason] = useState(
    invoice.hospitalisationReason ?? ""
  );
  const { data: hospitalisationTypes } =
    ApiAppointment.GetHospitalisationTypes.Hook();

  return (
    <CustomModal
      component={
        <InvoiceCardComponent invoice={invoice} isEditing={isEditing!} />
      }
      onOk={() =>
        alertDialogCtrl.open({
          message: "Are you sure you want to edit this date?",
          proceedBtnText: "Save",
          type: "danger",
          onSuccess: () => {
            let updatedInvoice = { ...invoice, isUnchecked: isUnchecked };
            if (!isUnchecked) {
              updatedInvoice.taskStartTime =
                UtilityDate.ToIsoUtcDateTime(startTime);
              updatedInvoice.taskEndTime =
                UtilityDate.ToIsoUtcDateTime(endTime);
              updatedInvoice.isHospitalised = false;
              updatedInvoice.hospitalisationType = null;
              updatedInvoice.hospitalisationReason = null;
            } else if (isHospitalized) {
              updatedInvoice.isHospitalised = true;
              updatedInvoice.hospitalisationType = hospitalizationType;
              if (
                updatedInvoice.hospitalisationType?.name.toLowerCase() ===
                "other"
              ) {
                updatedInvoice.hospitalisationReason = hospitalizationReason;
              }
            }
            handleInvoiceClick(updatedInvoice);
          },
        })
      }
      titleHeader={
        <div className="d-flex justify-content-between align-items-center">
          <Modal.Title className="w-auto">Edit Date</Modal.Title>
          <p className="small w-auto">
            Date: {UtilityDate.getDayMonth(invoice.taskStartTime!)} | Start
            Time: {UtilityDate.formatTime(invoice.taskStartTime)} | End Time:{" "}
            {UtilityDate.formatTime(invoice.taskEndTime)}
          </p>
        </div>
      }
    >
      <p>
        Uncheck this date?{" "}
        <Toggle
          className="ms-2"
          checked={isUnchecked}
          onChange={(val) => {
            setIsUnchecked((prev) => !prev);
            setIsHospitalized(false);
            setHospitalizationType(undefined);
            setHospitalizationReason("");
          }}
        />
      </p>
      {isUnchecked ? (
        <div className="mt-2">
          <p>
            Is patient hospitalized?{" "}
            <Toggle
              className="ms-2"
              checked={isHospitalized}
              onChange={(val) => {
                setHospitalizationType(undefined);
                setIsHospitalized(val);
                setHospitalizationReason("");
              }}
            />
          </p>
          {isHospitalized && (
            <div className="mt-2">
              <p>Hospitalization reason:</p>
              <SelectPicker
                data={
                  hospitalisationTypes?.map((item) => ({
                    value: item.sKey,
                    label: item.displayName || item.name,
                  })) ?? []
                }
                value={hospitalizationType?.sKey || ""}
                onChange={(val) => {
                  setHospitalizationReason("");
                  setHospitalizationType(
                    hospitalisationTypes?.find((item) => item.sKey === val)
                  );
                }}
                block
              />
            </div>
          )}
          {hospitalizationType &&
            hospitalisationTypes
              ?.find((item) => item.sKey === hospitalizationType.sKey)
              ?.name.toLowerCase() === "other" && (
              <div className="mt-2">
                <p>Hospitalization reason:</p>
                <Input
                  placeholder="Hospitalization reason"
                  value={hospitalizationReason}
                  onChange={(val) => setHospitalizationReason(val as string)}
                />
              </div>
            )}
        </div>
      ) : (
        <div className="mt-2 d-flex">
          <div className="flex-grow-1 me-1">
            <p>Start time: </p>
            <DatePicker
              value={startTime}
              onChangeCalendarDate={setStartTime}
              disabledHours={(hours) => !!hours && hours > endTime.getHours()}
              disabledMinutes={(minutes) =>
                !!minutes && minutes > endTime.getMinutes()
              }
              format="hh:mm aa"
              className="w-100 mt-1"
              oneTap
              ranges={[]}
            />
          </div>
          <div className="flex-grow-1 ms-1">
            <p>End time: </p>
            <DatePicker
              value={endTime}
              onChangeCalendarDate={setEndTime}
              disabledHours={(hour) => !!hour && hour < startTime.getHours()}
              disabledMinutes={(minute) =>
                !!minute && minute < startTime.getMinutes()
              }
              format="hh:mm aa"
              className="w-100 mt-1"
              oneTap
              ranges={[]}
            />
          </div>
        </div>
      )}
    </CustomModal>
  );
}

const InvoiceCardComponent = ({
  invoice,
  isEditing,
}: {
  invoice: ApiSchemaTaskDate;
  isEditing: boolean;
}) => {
  const {
    taskStartTime,
    taskEndTime,
    hospitalisationType,
    hospitalisationReason,
    isHospitalised,
    isUnchecked,
  } = invoice;

  return (
    <div
      className={`p-2 ${
        isEditing ? "pt-0" : ""
      } m-1 d-flex flex-column justify-content-center text-center invoice${
        isUnchecked ? "-unchecked" : ""
      }`}
    >
      {isEditing && (
        <span className="align-self-end">
          <img src={editIcon} alt="Update" className="me-1 click" height={15} />
        </span>
      )}
      <span>{UtilityDate.formatHumanFriendlyDate(taskStartTime)}</span>
      <span>
        {formatAMPM(new Date(taskStartTime || new Date()))} -{" "}
        {formatAMPM(new Date(taskEndTime || new Date()))}
      </span>
      {isHospitalised && !!hospitalisationType && (
        <span>
          (
          {!hospitalisationReason ||
          !(hospitalisationType.name.toLowerCase() === "other")
            ? hospitalisationType.displayName || hospitalisationType.name
            : hospitalisationReason}
          )
        </span>
      )}
    </div>
  );
};
